import {
  Container,
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
  Fade,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import LinkIcon from "@material-ui/icons/Link";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import LocationOnOutlinedIcon from "@material-ui/icons/LocationOnOutlined";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loading } from "../../components/shared/loading";
import parse from "html-react-parser";
import { HeroParticles } from "../../components/shared/HeroParticles";
import { SocialShare } from "../../components/shared/SocialShare";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const formatLabel = (str) => {
  if (!str) return "";
  return str
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const useStyles = makeStyles((theme) => ({
  // ── HERO ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "32vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "26vh",
    },
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "40px 20px",
    maxWidth: 740,
    [theme.breakpoints.down("xs")]: {
      padding: "28px 16px",
    },
  },
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  heroTitle: {
    fontSize: "clamp(1.3rem, 3.5vw, 2rem)",
    fontWeight: 700,
    marginBottom: 12,
    lineHeight: 1.3,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroBadges: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 8,
    marginTop: 4,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.68rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.8)",
    background: "rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "5px 14px",
    letterSpacing: "0.3px",
    backdropFilter: "blur(4px)",
    "& svg": {
      fontSize: "0.82rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.62rem",
      padding: "4px 10px",
    },
  },
  backBtn: {
    position: "absolute",
    top: 20,
    left: 20,
    color: "rgba(255,255,255,0.6)",
    fontSize: "0.72rem",
    fontWeight: 500,
    textTransform: "none",
    letterSpacing: "0.2px",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#ffffff",
      background: "rgba(255,255,255,0.08)",
    },
    "& svg": {
      fontSize: "1rem",
      marginRight: 4,
    },
    [theme.breakpoints.down("xs")]: {
      top: 12,
      left: 12,
      fontSize: "0.66rem",
    },
  },

  // ── CONTENT ──
  contentSection: {
    padding: "48px 0 0",
    background: "#ffffff",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 0 0",
    },
  },
  contentCard: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "0 24px",
    [theme.breakpoints.down("xs")]: {
      padding: "0 16px",
    },
  },
  jobBody: {
    fontSize: "0.82rem",
    color: "#444",
    lineHeight: 1.85,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#1a1a1a",
      margin: "22px 0 10px",
    },
    "& h1:first-child, & h2:first-child, & h3:first-child": {
      marginTop: 0,
    },
    "& ul, & ol": {
      paddingLeft: 24,
      margin: "10px 0",
    },
    "& li": {
      fontSize: "0.82rem",
      color: "#555",
      marginBottom: 6,
      lineHeight: 1.75,
    },
    "& p": {
      margin: "0 0 12px",
    },
    "& strong, & b": {
      color: "#333",
      fontWeight: 600,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.78rem",
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        fontSize: "0.88rem",
      },
      "& li": {
        fontSize: "0.78rem",
      },
    },
  },

  // ── SHARE BAR ──
  shareBar: {
    maxWidth: 800,
    margin: "0 auto",
    padding: "24px 24px 0",
    display: "flex",
    alignItems: "center",
    gap: 10,
    [theme.breakpoints.down("xs")]: {
      padding: "20px 16px 0",
      gap: 8,
    },
  },
  shareLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#aaa",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginRight: 2,
  },
  shareBtn: {
    transition: "all 0.2s ease",
    opacity: 0.65,
    "&:hover": {
      opacity: 1,
      transform: "scale(1.12)",
    },
  },

  // ── APPLY FORM ──
  applySection: {
    maxWidth: 820,
    margin: "0 auto",
    padding: "40px 24px 80px",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 16px 48px",
    },
  },
  applyCard: {
    position: "relative",
    background:
      "linear-gradient(160deg, #ffffff 0%, #fdfafd 55%, #f8eef8 100%)",
    borderRadius: 20,
    padding: "36px 36px 38px",
    border: "1px solid rgba(76,0,76,0.08)",
    boxShadow: "0 12px 40px rgba(76,0,76,0.08)",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 22px 30px",
      borderRadius: 16,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: -90,
      right: -90,
      width: 260,
      height: 260,
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(199,125,199,0.22), transparent 70%)",
      filter: "blur(40px)",
      pointerEvents: "none",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -100,
      left: -100,
      width: 280,
      height: 280,
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(76,0,76,0.12), transparent 70%)",
      filter: "blur(50px)",
      pointerEvents: "none",
    },
  },
  applyLabel: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.8px",
    marginBottom: 8,
  },
  applyTitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#4c004c",
    marginBottom: 6,
    letterSpacing: "-0.01em",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.2rem",
    },
  },
  applySubtitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.83rem",
    color: "#666",
    marginBottom: 28,
    lineHeight: 1.65,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.78rem",
      marginBottom: 22,
    },
  },
  formRow: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    gap: 16,
    marginBottom: 16,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: 14,
    },
  },
  formField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "rgba(255,255,255,0.7)",
      fontSize: "0.86rem",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      "& fieldset": {
        borderColor: "rgba(76,0,76,0.12)",
        transition: "border-color 0.3s ease, border-width 0.3s ease",
      },
      "&:hover fieldset": {
        borderColor: "rgba(76,0,76,0.32)",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 4px rgba(155,59,155,0.08)",
        "& fieldset": {
          borderColor: "#4c004c",
          borderWidth: 1.5,
        },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "13px 12px",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.83rem",
      color: "#888",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#4c004c",
    },
    "& .MuiInputAdornment-root svg": {
      color: "#9b3b9b",
      fontSize: 20,
      transition: "color 0.3s ease",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root svg": {
      color: "#4c004c",
    },
  },
  uploadBox: {
    position: "relative",
    zIndex: 1,
    border: "1.5px dashed rgba(76,0,76,0.22)",
    borderRadius: 14,
    padding: "20px 22px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    background: "rgba(255,255,255,0.5)",
    marginTop: 6,
    marginBottom: 22,
    "&:hover": {
      borderColor: "#9b3b9b",
      background: "rgba(155,59,155,0.04)",
      transform: "translateY(-1px)",
    },
    "&:hover $uploadIconWrap": {
      background: "rgba(155,59,155,0.18)",
      transform: "scale(1.04)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "16px 14px",
      gap: 12,
    },
  },
  uploadBoxFilled: {
    borderStyle: "solid",
    borderColor: "rgba(76,0,76,0.4)",
    background: "rgba(155,59,155,0.06)",
  },
  uploadIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(155,59,155,0.12)",
    color: "#4c004c",
    flexShrink: 0,
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 22 },
  },
  uploadInfo: {
    flex: 1,
    minWidth: 0,
  },
  uploadText: {
    fontSize: "0.82rem",
    color: "#666",
    "& strong": {
      color: "#4c004c",
      fontWeight: 600,
    },
  },
  uploadHint: {
    fontSize: "0.7rem",
    color: "#999",
    marginTop: 2,
  },
  uploadFileName: {
    fontSize: "0.78rem",
    color: "#4c004c",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 6,
    "& svg": {
      fontSize: 16,
      color: "#4caf50",
    },
  },
  submitBtn: {
    position: "relative",
    zIndex: 1,
    background: "linear-gradient(135deg, #4c004c 0%, #6b1b6b 100%)",
    color: "#ffffff",
    borderRadius: 14,
    padding: "14px 36px",
    fontSize: "0.9rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.2px",
    width: "100%",
    boxShadow: "0 8px 22px rgba(76,0,76,0.28)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "& .MuiButton-endIcon": {
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      marginLeft: 10,
    },
    "&:hover": {
      background: "linear-gradient(135deg, #6b1b6b 0%, #9b3b9b 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 14px 32px rgba(76,0,76,0.4)",
    },
    "&:hover .MuiButton-endIcon": {
      transform: "translateX(5px)",
    },
    "&:disabled": {
      background: "#cccccc",
      boxShadow: "none",
      color: "#ffffff",
      transform: "none",
    },
  },
  submitFootnote: {
    position: "relative",
    zIndex: 1,
    marginTop: 14,
    textAlign: "center",
    fontSize: "0.72rem",
    color: "#888",
    letterSpacing: "0.2px",
    "& strong": {
      color: "#4c004c",
      fontWeight: 600,
    },
  },

  // ── NOT FOUND ──
  notFound: {
    textAlign: "center",
    padding: "80px 20px",
  },
  notFoundText: {
    fontSize: "1rem",
    color: "#999",
    fontWeight: 500,
    marginBottom: 16,
  },
  notFoundBtn: {
    color: "#4c004c",
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "none",
    borderColor: "rgba(76,0,76,0.3)",
    borderRadius: 10,
    padding: "8px 24px",
    "&:hover": {
      borderColor: "#4c004c",
      background: "rgba(76,0,76,0.03)",
    },
  },
}));

export const JobDetail = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [isloading, setIsloading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  // Apply form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    linkedin_url: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const [tooltipText, setTooltipText] = useState("Click to copy the URL");

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsloading(true);
    setNotFound(false);

    const metaForm = new FormData();
    metaForm.append("page", 9);
    try {
      const metaRes = await Axios.post("/api/getmetadata", metaForm, {
        headers: { headKey: headKey },
      });
      if (metaRes.data.status) {
        setMetaTitle(metaRes.data.data.meta_title);
        setMetaKeywords(metaRes.data.data.meta_keywords);
        setMetaDesc(metaRes.data.data.meta_desc);
      }
    } catch (err) {}

    try {
      const res = await Axios.get("/api/get_jobs", {
        headers: { headKey: headKey },
      });
      if (res.data.status) {
        const found = res.data.data.find(
          (j) => String(j.id) === String(id)
        );
        if (found) {
          setJob(found);
        } else {
          setNotFound(true);
        }
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setIsloading(false);
      handleClick();
    }
  };

  const handleClick = () => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setTooltipText("Link copied to clipboard!");
        setTimeout(() => setTooltipText("Click to copy the URL"), 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      linkedin_url: "",
    });
    setCvFile(null);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCvChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCvFile(e.target.files[0]);
    }
  };

  const handleApply = async () => {
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.mobile ||
      !cvFile
    ) {
      setSnackMsg("Please fill all required fields and upload your CV.");
      setSnackSeverity("error");
      setSnackOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      fd.append("first_name", formData.first_name);
      fd.append("last_name", formData.last_name);
      fd.append("email", formData.email);
      fd.append("mobile", formData.mobile);
      fd.append("cv", cvFile);
      if (formData.linkedin_url) {
        fd.append("linkedin_url", formData.linkedin_url);
      }

      const res = await Axios.post("/api/apply_job", fd, {
        headers: { headKey: headKey, "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        setSnackMsg(res.data.msg || "Applied successfully!");
        setSnackSeverity("success");
        setSnackOpen(true);
        resetForm();
      } else {
        const msg =
          res.data.msg || "Something went wrong. Please try again.";
        const isDuplicate = msg.toLowerCase().includes("already applied");
        setSnackMsg(msg);
        setSnackSeverity(isDuplicate ? "warning" : "error");
        setSnackOpen(true);
      }
    } catch (err) {
      setSnackMsg("Something went wrong. Please try again.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (isloading) {
    return (
      <div>
        <Helmet>
          <title>{metaTitle || "Careers"}</title>
        </Helmet>
        <Loading />
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div>
        <Helmet>
          <title>Job Not Found</title>
        </Helmet>
        <Box className={classes.heroSection}>
          <HeroParticles id="jobdetail-notfound-particles" />
          <Button className={classes.backBtn} onClick={() => navigate("/careers")}>
            <ArrowBackIcon /> Back to Careers
          </Button>
          <Fade in={true} timeout={1000}>
            <Box className={classes.heroContent}>
              <Typography className={classes.heroTitle}>Careers</Typography>
            </Box>
          </Fade>
        </Box>
        <Box className={classes.notFound}>
          <Typography className={classes.notFoundText}>
            This job posting is no longer available or doesn't exist.
          </Typography>
          <Button
            variant="outlined"
            className={classes.notFoundBtn}
            onClick={() => navigate("/careers")}
          >
            Browse Open Positions
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>
          {job.title}
          {metaTitle ? ` - ${metaTitle}` : ""}
        </title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords} />
      </Helmet>

      {/* Hero */}
      <Box className={classes.heroSection}>
        <HeroParticles id="jobdetail-hero-particles" />
        <Button className={classes.backBtn} onClick={() => navigate("/careers")}>
          <ArrowBackIcon /> Back to Careers
        </Button>
        <Fade in={true} timeout={1000}>
          <Box className={classes.heroContent}>
            <Typography className={classes.heroTitle}>{job.title}</Typography>
            <Box className={classes.heroBadges}>
              {job.job_type && (
                <Box className={classes.heroBadge}>
                  <BusinessCenterIcon />
                  {formatLabel(job.job_type)}
                </Box>
              )}
              {job.work_type && (
                <Box className={classes.heroBadge}>
                  <WorkOutlineIcon />
                  {formatLabel(job.work_type)}
                </Box>
              )}
              {job.location && (
                <Box className={classes.heroBadge}>
                  <LocationOnOutlinedIcon />
                  {job.location}
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Job Description */}
      <Box className={classes.contentSection}>
        <Fade in={true} timeout={800}>
          <Box className={classes.contentCard}>
            <Box className={classes.jobBody}>
              {job.description ? parse(job.description) : ""}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Share Bar */}
      <Box className={classes.shareBar}>
        <SocialShare url={window.location.href} />
      </Box>

      {/* Apply Form */}
      <Box className={classes.applySection}>
        <Fade in={true} timeout={1000}>
          <Box className={classes.applyCard}>
            <Typography className={classes.applyLabel}>
              Apply Now
            </Typography>
            <Typography className={classes.applyTitle}>
              Apply for this position
            </Typography>
            <Typography className={classes.applySubtitle}>
              Tell us about yourself and attach your CV. We'll review and get back within a few business days.
            </Typography>

            <Box className={classes.formRow}>
              <TextField
                label="First Name"
                name="first_name"
                variant="outlined"
                size="small"
                required
                value={formData.first_name}
                onChange={handleFormChange}
                className={classes.formField}
                InputLabelProps={{ shrink: true }}
                placeholder="John"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Last Name"
                name="last_name"
                variant="outlined"
                size="small"
                required
                value={formData.last_name}
                onChange={handleFormChange}
                className={classes.formField}
                InputLabelProps={{ shrink: true }}
                placeholder="Doe"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box className={classes.formRow}>
              <TextField
                label="Email"
                name="email"
                type="email"
                variant="outlined"
                size="small"
                required
                value={formData.email}
                onChange={handleFormChange}
                className={classes.formField}
                InputLabelProps={{ shrink: true }}
                placeholder="john@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Mobile"
                name="mobile"
                variant="outlined"
                size="small"
                required
                value={formData.mobile}
                onChange={handleFormChange}
                className={classes.formField}
                InputLabelProps={{ shrink: true }}
                placeholder="+91 98765 43210"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box className={classes.formRow}>
              <TextField
                label="LinkedIn URL (Optional)"
                name="linkedin_url"
                variant="outlined"
                size="small"
                value={formData.linkedin_url}
                onChange={handleFormChange}
                className={classes.formField}
                InputLabelProps={{ shrink: true }}
                placeholder="https://linkedin.com/in/yourprofile"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkedInIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* CV Upload */}
            <input
              type="file"
              id="cv-upload"
              accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleCvChange}
            />
            <label htmlFor="cv-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  cvFile ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {cvFile ? <InsertDriveFileIcon /> : <CloudUploadIcon />}
                </Box>
                <Box className={classes.uploadInfo}>
                  {cvFile ? (
                    <>
                      <Typography className={classes.uploadFileName}>
                        <CheckCircleIcon />
                        {cvFile.name}
                      </Typography>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography className={classes.uploadText}>
                        <strong>Upload your CV</strong>
                      </Typography>
                      <Typography className={classes.uploadHint}>
                        PDF or DOC, up to 5MB - Required
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>

            <Button
              className={classes.submitBtn}
              onClick={handleApply}
              disabled={submitting}
              endIcon={!submitting && <ArrowForwardIcon />}
            >
              {submitting ? (
                <CircularProgress size={20} style={{ color: "#fff" }} />
              ) : (
                "Submit Application"
              )}
            </Button>
            <Typography className={classes.submitFootnote}>
              Your details are kept confidential. We typically respond in <strong>3-5 business days</strong>.
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          style={{
            borderRadius: 10,
            fontSize: "0.8rem",
            fontWeight: 500,
          }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};
